import { API_BASE } from "config/api";
import { withAuthHeaders } from "services/authHeader";

/* ----------------------------------------------------
   GET distinct sample statuses
---------------------------------------------------- */
export default async function getStatuses() {
  try {
    const url = new URL("api/samples/getSampleStatuses", API_BASE);
    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const json = await res.json();
    if (json.status === "success") return json.data;
    throw new Error(json.message || "Failed to fetch sample statuses");
  } catch (error) {
    console.error("Error in getStatuses:", error);
    throw error;
  }
}

export async function getAnnotatorSampleCount(annotatorId) {
  try {
    const url = new URL("api/samples/getAnnotatorSampleCount", API_BASE);
    url.searchParams.append("annotatorId", annotatorId);
    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });
    const json = await res.json();
    if (json.status === "success") {
      return {
        total_count: json.total_count,
        data_summary: json.data_summary,
      };
    }
    throw new Error(json.message || "Failed to fetch annotator sample count");
  } catch (error) {
    console.error("Error in getAnnotatorSampleCount:", error);
    throw error;
  }
}

/* ----------------------------------------------------
   TRANSFER samples from one user to another
---------------------------------------------------- */
export async function transferSamples(fromUserId, toUserId) {
  try {
    const url = new URL("api/samples/transferSamples", API_BASE);
    url.searchParams.append("fromUserId", fromUserId);
    url.searchParams.append("toUserId", toUserId);

    const res = await fetch(url, {
      method: "PATCH",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const json = await res.json();

    if (json.status === "success") {
      return {
        status: json.status,
        message: json.message,
      };
    }

    throw new Error(json.message || "Failed to transfer samples");
  } catch (error) {
    console.error("Error in transferSamples:", error);
    throw error;
  }
}

/* ----------------------------------------------------
   REASSIGN only not-started (Pending) samples from one
   annotator to another (both owned by the logged-in admin)
---------------------------------------------------- */
export async function reassignPendingSamples(fromUserId, toUserId) {
  try {
    const url = new URL("api/samples/reassignPendingSamples", API_BASE);
    url.searchParams.append("fromUserId", fromUserId);
    url.searchParams.append("toUserId", toUserId);

    const res = await fetch(url, {
      method: "PATCH",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const json = await res.json();

    if (json.status === "success") {
      return {
        status: json.status,
        message: json.message,
        moved_count: json.moved_count,
      };
    }

    throw new Error(json.message || "Failed to reassign samples");
  } catch (error) {
    console.error("Error in reassignPendingSamples:", error);
    throw error;
  }
}

/* ----------------------------------------------------
   GET samples list with filters
---------------------------------------------------- */
export async function getSamples(filters = {}) {
  try {
    const url = new URL("api/samples/getSamples", API_BASE);
    const params = new URLSearchParams({
      state: filters.state || "",
      status: filters.status || "",
      annotator: filters.annotator || "",
      village: filters.village || "",
      discrepancy: filters.discrepancy || "",
      language: filters.language || "",
      page: filters.page || 1,
      limit: filters.limit || 10,
    });
    url.search = params.toString();

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const json = await res.json();
    if (json.status === "success") return json.data;
    throw new Error(json.message || "Failed to fetch samples");
  } catch (error) {
    console.error("Error in getSamples:", error);
    throw error;
  }
}

/* ----------------------------------------------------
   GET sample count by state
---------------------------------------------------- */
export async function getStateSampleCount(state) {
  try {
    const url = new URL("api/samples/getStateSampleCount", API_BASE);
    url.searchParams.set("state", state || "");

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const json = await res.json();
    if (json.status === "success") return json;
    throw new Error(json.message || "Failed to fetch state sample count");
  } catch (error) {
    console.error("Error in getStateSampleCount:", error);
    throw error;
  }
}

/* ----------------------------------------------------
   PATCH assign samples to annotators
---------------------------------------------------- */
export async function assignSamples(sampleObj) {
  try {
    const url = new URL("api/samples/assignSamples", API_BASE);
    const res = await fetch(url, {
      method: "PATCH",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
      body: JSON.stringify(sampleObj),
    });

    const json = await res.json();
    if (json.status === "success") return json;
    throw new Error(json.message || "Failed to assign samples");
  } catch (error) {
    console.error("Error in assignSamples:", error);
    throw error;
  }
}

/* ----------------------------------------------------
   POST get sample details
---------------------------------------------------- */
export async function getSampleDetails(sampleId, sampleType) {
  try {
    const url = new URL("api/samples/getSampleDetails", API_BASE);
    url.searchParams.set("sample_id", sampleId || "");
    url.searchParams.set("sample_type", sampleType || "");

    const res = await fetch(url, {
      method: "POST",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const json = await res.json();
    if (json.status === "success") return json;
    throw new Error(json.message || "Failed to fetch sample details");
  } catch (error) {
    console.error("Error in getSampleDetails:", error);
    throw error;
  }
}

/* ----------------------------------------------------
   PATCH edit sample details
---------------------------------------------------- */
export async function editSampleDetails(sampleList) {
  try {
    const url = new URL("api/samples/editSampleDetails", API_BASE);
    const res = await fetch(url, {
      method: "PATCH",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
      body: JSON.stringify(sampleList),
    });

    const json = await res.json();
    if (json.status === "success") {
      return json;
    } else {
      alert(json.message);
      throw new Error(json.message || "Failed to edit sample details");
    }
  } catch (error) {
    console.error("Error in editSampleDetails:", error);
    throw error;
  }
}

/* ----------------------------------------------------
   GET student exams with filters
   /samples/getStudentExams?page=1&limit=25&status=&studentid=&annotator1_id=&annotator2_id=&pending_assignment=true
---------------------------------------------------- */
export async function getStudentExams(filters = {}) {
  try {
    const url = new URL("api/samples/getStudentExams", API_BASE);

    // Build query params
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.studentid) params.set("studentid", filters.studentid);
    if (filters.annotator1_id) params.set("annotator1_id", filters.annotator1_id);
    if (filters.annotator2_id) params.set("annotator2_id", filters.annotator2_id);
    if (filters.annotator_id) params.set("annotator_id", filters.annotator_id);
    if (filters.my_status) params.set("my_status", filters.my_status);
    if (filters.assigned_by_admin) params.set("assigned_by_admin", filters.assigned_by_admin);
    if (filters.exam_id) params.set("exam_id", filters.exam_id);
    if (filters.pending_assignment) params.set("pending_assignment", "true");

    params.set("page", String(filters.page || 1));
    params.set("limit", String(filters.limit || 25));

    url.search = params.toString();

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const json = await res.json();

    if (res.ok && json?.status === "success") {
      // return everything so caller can use metadata too
      return {
        data: json.data || [],
        total_records: json.total_records ?? (json.data?.length || 0),
        page: json.page || Number(params.get("page")),
        limit: json.limit || Number(params.get("limit")),
        raw: json,
      };
    }

    throw new Error(json?.message || `Failed to fetch student exams (${res.status})`);
  } catch (error) {
    console.error("Error in getStudentExams:", error);
    throw error;
  }
}

/* ----------------------------------------------------
   GET annotators from existing API: /users/getAllAnnotators
   Returns a simplified array: [{ id, name, email_id, language, state, ... }]
---------------------------------------------------- */
export async function getAnnotators() {
  try {
    const url = new URL("api/users/getAllAnnotators", API_BASE);
    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({ Accept: "application/json" }),
    });

    const json = await res.json();

    if (!res.ok || json?.status !== "success") {
      throw new Error(json?.message || `Failed to fetch annotators (${res.status})`);
    }

    const list = Array.isArray(json.data) ? json.data : [];

    // Map to the shape the UI expects
    return list.map((a) => {
      const hasName = (a.first_name && a.first_name.trim()) || (a.last_name && a.last_name.trim());
      const displayName = hasName
        ? `${a.first_name || ""} ${a.last_name || ""}`.trim()
        : a.email_id || `User #${a.id}`;

      return {
        id: a.id,
        name: displayName, // <-- used by the dropdowns
        email_id: a.email_id, // extra fields kept if you need them later
        role: a.role,
        language: a.language,
        state: a.state,
        district: a.district,
        block: a.block,
        program_id: a.program_id,
        program_name: a.program_name,
        phone_number: a.phone_number,
      };
    });
  } catch (error) {
    console.error("Error in getAnnotators:", error);
    throw error;
  }
}

export const getAllAnnotatorsDeleted = async () => {
  try {
    const url = new URL("api/users/getAllAnnotatorsDeleted", API_BASE);

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({ Accept: "application/json" }),
    });

    const json = await res.json();

    if (!res.ok || json?.status !== "success") {
      throw new Error(json?.message || "Failed to fetch deleted annotators");
    }

    return Array.isArray(json.data) ? json.data : [];
  } catch (error) {
    console.error("getAllAnnotatorsDeleted:", error);
    throw error;
  }
};

export const getAllAnnotatorsIncludingDeleted = async () => {
  try {
    const url = new URL("api/users/getAllAnnotatorsIncludingDeleted", API_BASE);

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const json = await res.json();

    if (!res.ok || json?.status !== "success") {
      throw new Error(json?.message || `Failed to fetch annotators (${res.status})`);
    }

    const list = Array.isArray(json.data) ? json.data : [];

    // Map to the shape the UI expects
    return list.map((a) => {
      const hasName = (a.first_name && a.first_name.trim()) || (a.last_name && a.last_name.trim());
      const displayName = hasName
        ? `${a.first_name || ""} ${a.last_name || ""}`.trim()
        : a.email_id || `User ${a.id}`;

      return {
        id: a.id,
        name: displayName,
        email_id: a.email_id || "",
        language: a.language || "",
        state: a.state || "",
        district: a.district || "",
        village: a.village || "",
        program_id: a.program_id || null,
        program_name: a.program_name || "",
        assigned_samples: a.assigned_samples || 0,
        is_deleted: a.is_deleted || false,
      };
    });
  } catch (error) {
    console.error("Error in getAllAnnotatorsIncludingDeleted:", error);
    throw error;
  }
};

export const getAllAnnotatorsByAdmin = async (adminId = null) => {
  try {
    let url = new URL("api/users/getAllAnnotatorsByAdmin", API_BASE);

    if (adminId) {
      url += `?adminId=${adminId}`;
    }

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const json = await res.json();

    if (!res.ok || json?.status !== "success") {
      throw new Error(json?.message || `Failed to fetch annotators (${res.status})`);
    }

    const list = Array.isArray(json.data) ? json.data : [];

    // Map to the shape the UI expects
    return list.map((a) => {
      const hasName = (a.first_name && a.first_name.trim()) || (a.last_name && a.last_name.trim());
      const displayName = hasName
        ? `${a.first_name || ""} ${a.last_name || ""}`.trim()
        : a.email_id || `User #${a.id}`;

      return {
        id: a.id,
        name: displayName, // <-- used by the dropdowns
        email_id: a.email_id, // extra fields kept if you need them later
        role: a.role,
        language: a.language,
        state: a.state,
        district: a.district,
        block: a.block,
        program_id: a.program_id,
        program_name: a.program_name,
        phone_number: a.phone_number,
      };
    });
  } catch (error) {
    console.error("Error in getAnnotators:", error);
    throw error;
  }
};

export const getAllAnnotatorsByAdminDeleted = async (adminId) => {
  try {
    const url = new URL("api/users/getAllAnnotatorsByAdminDeleted", API_BASE);
    if (adminId) url.searchParams.append("adminId", adminId);

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({ Accept: "application/json" }),
    });

    const json = await res.json();

    if (!res.ok || json?.status !== "success") {
      throw new Error(json?.message || "Failed to fetch deleted annotators");
    }

    return Array.isArray(json.data) ? json.data : [];
  } catch (error) {
    console.error("getAllAnnotatorsByAdminDeleted:", error);
    throw error;
  }
};

export const getAllAnnotatorsByAdminIncludingDeleted = async (adminId = null) => {
  try {
    let url = new URL("api/users/getAllAnnotatorsByAdminIncludingDeleted", API_BASE);

    if (adminId) {
      url += `?adminId=${adminId}`;
    }

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const json = await res.json();

    if (!res.ok || json?.status !== "success") {
      throw new Error(json?.message || `Failed to fetch annotators (${res.status})`);
    }

    const list = Array.isArray(json.data) ? json.data : [];

    // Map to the shape the UI expects
    return list.map((a) => {
      const hasName = (a.first_name && a.first_name.trim()) || (a.last_name && a.last_name.trim());
      const displayName = hasName
        ? `${a.first_name || ""} ${a.last_name || ""}`.trim()
        : a.email_id || `User #${a.id}`;

      return {
        id: a.id,
        name: displayName, // <-- used by the dropdowns
        email_id: a.email_id, // extra fields kept if you need them later
        role: a.role,
        language: a.language,
        state: a.state,
        district: a.district,
        block: a.block,
        program_id: a.program_id,
        program_name: a.program_name,
        phone_number: a.phone_number,
      };
    });
  } catch (error) {
    console.error("Error in getAnnotators:", error);
    throw error;
  }
};

export async function getAdmins() {
  try {
    const url = new URL("api/users/getAllAdmins", API_BASE);
    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({ Accept: "application/json" }),
    });

    const json = await res.json();

    if (!res.ok || json?.status !== "success") {
      throw new Error(json?.message || `Failed to fetch admins (${res.status})`);
    }

    const list = Array.isArray(json.data) ? json.data : [];

    // Map to the shape the UI expects
    return list.map((a) => {
      const hasName = (a.first_name && a.first_name.trim()) || (a.last_name && a.last_name.trim());
      const displayName = hasName
        ? `${a.first_name || ""} ${a.last_name || ""}`.trim()
        : a.email_id || `Admin #${a.id}`;

      return {
        id: a.id,
        name: displayName,
        email_id: a.email_id,
        first_name: a.first_name,
        last_name: a.last_name,
      };
    });
  } catch (error) {
    console.error("Error in getAdmins:", error);
    throw error;
  }
}

export async function assignStudentExams(payload) {
  const url = new URL("api/samples/assignStudentExams", API_BASE);

  const res = await fetch(url, {
    method: "POST",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify(payload),
  });

  const json = await res.json();

  //  HARD ERROR → throw backend payload
  if (!res.ok || json?.status === "error") {
    throw {
      status: json?.status || "error",
      code: json?.code,
      message: json?.message || `Failed to assign exams (${res.status})`,
    };
  }

  //  success OR warning
  return json;
}

export async function getStudentExamsForAnnotator(annotatorId, extra = {}) {
  if (!annotatorId) throw new Error("annotatorId is required");

  // We'll request a sufficiently large limit to bring "my" exams in one shot.
  // You can change 500 to what suits your dataset.
  const base = { page: 1, limit: extra.limit || 500, ...extra };

  const [a1, a2] = await Promise.all([
    getStudentExams({ ...base, annotator1_id: annotatorId, annotator2_id: "" }),
    getStudentExams({ ...base, annotator1_id: "", annotator2_id: annotatorId }),
  ]);

  const keyOf = (row) => row.id ?? row.exam_id ?? `${row.student_id}:${row.exam_id}`;

  const map = new Map();
  (a1.data || []).forEach((r) => map.set(keyOf(r), r));
  (a2.data || []).forEach((r) => map.set(keyOf(r), r));

  const merged = Array.from(map.values());

  return {
    data: merged,
    total_records: merged.length,
  };
}

export async function getExamBundle(exam_id) {
  const url = new URL("api/samples/getExamBundle", API_BASE);
  url.searchParams.set("exam_id", exam_id);

  const res = await fetch(url, {
    method: "GET",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
  });
  const json = await res.json();
  if (json.status === "success") return json;
  throw new Error(json.message || "Failed to fetch exam bundle");
}

export async function saveAnnotation(annotationData) {
  const url = new URL("api/samples/saveAnnotation", API_BASE);

  const res = await fetch(url, {
    method: "POST",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify(annotationData),
  });

  const json = await res.json();

  if (!res.ok) {
    throw new Error(json.message || `HTTP error! status: ${res.status}`);
  }

  if (json.status === "success") return json;
  throw new Error(json.message || "Failed to save annotation");
}

export async function getAnnotation(recording_id, annotator_id, round = 1) {
  const url = new URL("api/samples/getAnnotation", API_BASE);
  url.searchParams.set("recording_id", recording_id);
  url.searchParams.set("annotator_id", annotator_id);
  url.searchParams.set("round", round);

  const res = await fetch(url, {
    method: "GET",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
  });

  const json = await res.json();

  if (!res.ok) {
    throw new Error(json.message || `HTTP error! status: ${res.status}`);
  }

  if (json.status === "success") return json;
  throw new Error(json.message || "Failed to fetch annotation");
}

export async function getAnnotationsBulk(exam_id, annotator_id, round = 1) {
  const url = new URL("api/samples/getAnnotationsBulk", API_BASE);
  url.searchParams.set("exam_id", exam_id);
  url.searchParams.set("annotator_id", annotator_id);
  url.searchParams.set("round", round);

  const res = await fetch(url, {
    method: "GET",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
  });

  const json = await res.json();

  if (!res.ok) {
    throw new Error(json.message || `HTTP error! status: ${res.status}`);
  }

  if (json.status === "success") return json;
  throw new Error(json.message || "Failed to fetch annotations");
}

export async function markExamComplete(annotator_id, exam_id) {
  const url = new URL("api/samples/markExamComplete", API_BASE);

  const res = await fetch(url, {
    method: "POST",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify({ annotator_id, exam_id }),
  });

  const json = await res.json();

  if (!res.ok) {
    throw new Error(json.message || `HTTP error! status: ${res.status}`);
  }

  if (json.status === "success") return json;
  throw new Error(json.message || "Failed to mark exam complete");
}
/* ----------------------------------------------------
   POST save multiple annotations in bulk
---------------------------------------------------- */
export async function saveAnnotationsBulk(annotationsArray) {
  const url = new URL("api/samples/saveAnnotationsBulk", API_BASE);

  const res = await fetch(url, {
    method: "POST",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify({ annotations: annotationsArray }),
  });

  const json = await res.json();

  if (!res.ok) {
    throw new Error(json.message || `HTTP error! status: ${res.status}`);
  }

  if (json.status === "success") return json;
  throw new Error(json.message || "Failed to save annotations in bulk");
}

export async function getAdjacentExams(examId, annotatorId, filters = {}) {
  try {
    const url = new URL("api/samples/adjacentExams", API_BASE);

    const params = new URLSearchParams();
    params.set("exam_id", examId);
    params.set("annotator_id", annotatorId);

    if (Object.keys(filters).length > 0) {
      params.set("filter", JSON.stringify(filters));
    }

    url.search = params.toString();

    const res = await fetch(url, {
      method: "GET",
      headers: withAuthHeaders({
        "Content-Type": "application/json",
        Accept: "application/json",
      }),
    });

    const json = await res.json();

    if (res.ok && json?.status === "success") {
      return json.data;
    }

    throw new Error(json?.message || `Failed to fetch adjacent exams (${res.status})`);
  } catch (error) {
    console.error("Error in getAdjacentExams:", error);
    throw error;
  }
}

/* ----------------------------------------------------
   PATCH save remark for a recording
   Body: { recording_id, remarks }
---------------------------------------------------- */
export async function saveRemark(recording_id, remarks) {
  const url = new URL("api/samples/saveRemark", API_BASE);

  const res = await fetch(url, {
    method: "PATCH",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify({ recording_id, remarks }),
  });

  const json = await res.json();

  if (!res.ok || json?.status === "error") {
    throw new Error(json?.message || `Failed to save remark (${res.status})`);
  }

  return json;
}

/* ----------------------------------------------------
   PATCH resolve (blank) remark for a recording
   Body: { recording_id, annotator_id }
---------------------------------------------------- */
export async function resolveRemark(recording_id, annotator_id) {
  const url = new URL("api/samples/resolveRemark", API_BASE);

  const res = await fetch(url, {
    method: "PATCH",
    headers: withAuthHeaders({
      "Content-Type": "application/json",
      Accept: "application/json",
    }),
    body: JSON.stringify({ recording_id, annotator_id }),
  });

  const json = await res.json();

  if (!res.ok || json?.status === "error") {
    throw new Error(json?.message || `Failed to resolve remark (${res.status})`);
  }

  return json;
}
